import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Inbox,
  Headphones,
  UserPlus,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { cn } from "@/shared/lib/utils";
import { posterService, type TaskStatus } from "@/features/poster";
import { useAuth } from "@/contexts/AuthContext";
import { useKefuPending, type KefuPendingItem } from "@/entities/cs-pending";

const STORAGE_KEY = "poster:notif:seen:v1";
type SeenMap = Record<string, TaskStatus>;
type NotifItem = {
  id: number;
  name: string;
  status: TaskStatus;
  completedAt: string | null;
};

function loadSeen(): SeenMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveSeen(m: SeenMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(m));
  } catch {
    // quota exceeded; not fatal
  }
}

function isTerminal(s: TaskStatus): boolean {
  return s === "SUCCESS" || s === "PARTIAL" || s === "FAILED" || s === "CANCELLED";
}

/**
 * 海报任务通知铃(1010 起加「客服」分组:网页 / H5 访客等待人工、新留资,点进 `/ai-kefu?session=<id>`)。
 *
 * 30s 拉一次最近任务列表(size=20)。把"自上次打开 popover 后,从 PENDING/RUNNING
 * 转到终态"的任务记为未读,显示红点 + 数字。点击 popover 标记全部已读;点单条
 * 跳详情页。
 *
 * 状态对照表存 localStorage,跨刷新保留;清缓存重置即可。
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const seenRef = useRef<SeenMap>(loadSeen());

  // V26.3 — 海报任务通知是租户级业务,平台超管(scope=PLATFORM)无租户归属,
  // 调 /poster/tasks 会被后端 403 拒,Console 每 30s 报一次错。enabled 卡死。
  const isTenantScope = user?.scope === "TENANT";

  const q = useQuery({
    queryKey: ["poster-tasks", "notif"],
    queryFn: () => posterService.listTasks({ page: 0, size: 20 }),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    enabled: isTenantScope,
  });

  // 计算未读 = "上次记忆是 PENDING/RUNNING / 不存在" + 当前是终态。
  const unreadIds = useMemo(() => {
    const tasks = q.data?.records ?? [];
    const seen = seenRef.current;
    const fresh: number[] = [];
    for (const t of tasks) {
      if (!isTerminal(t.status)) continue;
      const prev = seen[String(t.id)];
      if (prev !== t.status) {
        fresh.push(t.id);
      }
    }
    return fresh;
  }, [q.data]);

  // popover 打开瞬间标记当前所见任务的状态为"已读"
  useEffect(() => {
    if (!open) return;
    const tasks = q.data?.records ?? [];
    const next = { ...seenRef.current };
    for (const t of tasks) {
      next[String(t.id)] = t.status;
    }
    // 顺手清掉 90 天前不在列表里的旧 key,避免无限增长
    const allowed = new Set(tasks.map((t) => String(t.id)));
    for (const k of Object.keys(next)) {
      if (!allowed.has(k) && Object.keys(next).length > 200) {
        delete next[k];
      }
    }
    seenRef.current = next;
    saveSeen(next);
  }, [open, q.data]);

  const items: NotifItem[] = (q.data?.records ?? [])
    .filter((task) => isTerminal(task.status))
    .slice(0, 10)
    .map((task) => ({
      id: task.id,
      name: task.name || t("notificationBell.taskFallback", { id: task.id }),
      status: task.status,
      completedAt: task.completedAt,
    }));

  // 1010 一期补完 B4:「客服」分组 —— 等待人工 / 新留资(/pending 的 latest);等待人工数计入角标。
  const kefuPending = useKefuPending();
  const kefuItems: KefuPendingItem[] = kefuPending?.latest ?? [];
  const kefuWaiting = kefuPending?.waitingHuman ?? 0;

  const unreadCount = unreadIds.length + kefuWaiting;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-muted-foreground"
          aria-label={
            unreadCount
              ? t("notificationBell.ariaWithCount", { count: unreadCount })
              : t("notificationBell.ariaLabel")
          }
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="fx-glow absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-destructive px-1 text-2xs font-bold text-destructive-foreground shadow-[0_0_10px_hsl(var(--destructive))]">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-semibold">{t("notificationBell.recentTasks")}</span>
          <span className="text-2xs text-muted-foreground">
            {q.isFetching ? t("notificationBell.refreshing") : t("notificationBell.autoRefresh")}
          </span>
        </div>
        {kefuItems.length > 0 && (
          <div className="border-b" data-testid="notif-kefu-group">
            <div className="flex items-center justify-between px-3 pt-2 pb-1">
              <span className="text-xs font-semibold text-muted-foreground">
                {t("notificationBell.kefuGroup")}
              </span>
              {kefuWaiting > 0 && (
                <span className="text-2xs text-destructive">
                  {t("notificationBell.kefuWaiting", { count: kefuWaiting })}
                </span>
              )}
            </div>
            <ul className="max-h-60 divide-y overflow-y-auto">
              {kefuItems.map((it) => (
                <li key={`${it.kind}-${it.sessionId}-${it.at}`}>
                  <button
                    type="button"
                    className={cn(
                      "w-full px-3 py-2 text-left transition-colors hover:bg-accent/30",
                      it.kind === "waiting_human" && "bg-destructive/5"
                    )}
                    onClick={() => {
                      setOpen(false);
                      navigate(`/ai-kefu?session=${encodeURIComponent(it.sessionId)}`);
                    }}
                  >
                    <div className="flex items-start gap-2">
                      {it.kind === "lead" ? (
                        <UserPlus className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                      ) : (
                        <Headphones className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm">
                          {it.kind === "lead"
                            ? t("notificationBell.kefuLead", { name: it.customerName })
                            : t("notificationBell.kefuWaitingItem", { name: it.customerName })}
                          {it.entryName ? (
                            <span className="text-muted-foreground"> · {it.entryName}</span>
                          ) : null}
                        </div>
                        <div className="mt-0.5 truncate text-2xs text-muted-foreground">
                          {it.lastMessage} · {relTime(it.at, t)}
                        </div>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-sm text-muted-foreground">
            <Inbox className="h-6 w-6 opacity-40" />
            {t("notificationBell.noFinishedTasks")}
          </div>
        ) : (
          <ul className="max-h-80 divide-y overflow-y-auto">
            {items.map((it) => (
              <li key={it.id}>
                <button
                  type="button"
                  className={cn(
                    "w-full px-3 py-2 text-left transition-colors hover:bg-accent/30",
                    unreadIds.includes(it.id) && "bg-warning/5"
                  )}
                  onClick={() => {
                    setOpen(false);
                    navigate(`/ai-haibao/tasks/${it.id}`);
                  }}
                >
                  <div className="flex items-start gap-2">
                    <StatusIcon status={it.status} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm">{it.name}</div>
                      <div className="mt-0.5 text-2xs text-muted-foreground">
                        {labelOf(it.status, t)} · {relTime(it.completedAt, t)}
                      </div>
                    </div>
                    {unreadIds.includes(it.id) && (
                      <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-warning" />
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="border-t px-3 py-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-full justify-center text-xs"
            onClick={() => {
              setOpen(false);
              navigate("/ai-haibao");
            }}
          >
            {t("notificationBell.viewAll")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function StatusIcon({ status }: { status: TaskStatus }) {
  if (status === "SUCCESS") {
    return <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />;
  }
  if (status === "PARTIAL") {
    return <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />;
  }
  return <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />;
}

type TFn = ReturnType<typeof useTranslation>["t"];

function labelOf(s: TaskStatus, t: TFn): string {
  switch (s) {
    case "SUCCESS":
      return t("status.success");
    case "PARTIAL":
      return t("status.partial");
    case "FAILED":
      return t("status.failed");
    case "CANCELLED":
      return t("status.cancelled");
    default:
      return s;
  }
}

function relTime(iso: string | null, t: TFn): string {
  if (!iso) return t("notificationBell.noDate");
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return t("notificationBell.noDate");
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return t("notificationBell.justNow");
  if (m < 60) return t("notificationBell.minutesAgo", { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t("notificationBell.hoursAgo", { count: h });
  return new Date(iso).toLocaleDateString();
}
